<script setup lang="ts">
import type {
  BreakpointInterface,
  currentMediaObjectInterface,
  MediaFieldBreakpointConfigInterface,
  MediaFieldPropertiesBreakpointInterface,
  VoixMediaFieldInterface,
} from '@voix/types'
import { useImage } from '#imports'
import {
  getApplicableBreakpoints,
  getGreaterThanBreakpoints,
} from '@voix/composables/useBreakpoints'

import { useBreakpointStore } from '@voix/store/breakpointStore'

import { useWindowSize } from '@vueuse/core'
import { computed, inject } from 'vue'

import VoixCloudflare from '../../video/VoixCloudflare.vue'
import VoixVimeo from '../../video/VoixVimeo.vue'
import VoixWistia from '../../video/VoixWistia.vue'
import VoixYouTube from '../../video/VoixYouTube.vue'

const props = defineProps<{
  field?: VoixMediaFieldInterface
  currentBreakpoint?: string
  background?: boolean
  loading?: 'lazy' | 'eager'
  debug: boolean
}>()

const emit = defineEmits([
  'load',
])

const nuxtImage = useImage()
const breakpointStore = useBreakpointStore()
const { width: windowWidth } = useWindowSize()

const orderedMediaFieldConfig = computed(() => {
  const mediaField = props.field as VoixMediaFieldInterface
  const mediaConfig = mediaField.config?.breakpoints

  if (!mediaConfig)
    return null

  const orderedBreakpoints = ['default', 'sm', 'md', 'lg', 'xl', '2xl']
  const orderedMediaConfig = Object.fromEntries(
    orderedBreakpoints
      .filter(key => key in mediaConfig)
      .map(key => [key, mediaConfig[key]])
  )

  return orderedMediaConfig
})

function getBreakpointConfig(breakpoint: string) {
  if (props.field === null)
    return null

  const mediaField = props.field as VoixMediaFieldInterface
  if (mediaField.config?.breakpoints) {
    const breakpointConfig = mediaField.config.breakpoints[breakpoint]
    if (breakpointConfig)
      return breakpointConfig

    // Check in the reverse of applicable breakpoints to see if there is a media
    const applicableBreakpoints = getApplicableBreakpoints(
      windowWidth.value,
    ).reverse()

    // find the first breakpoint that is in mediaField.config?.breakpoints
    for (const applicableBreakpoint of applicableBreakpoints) {
      if (mediaField.config?.breakpoints[applicableBreakpoint.name]) {
        return mediaField.config?.breakpoints[applicableBreakpoint.name]
      }
    }
  }

  return null
}

function getBreakpointValue(breakpoint: string) {
  if (props.field === null)
    return null

  const mediaField = props.field as VoixMediaFieldInterface
  if (mediaField.value && Array.isArray(mediaField.value)) {
    const breakpointValue = mediaField.value.find(
      b => b.breakpoint === breakpoint,
    )

    if (breakpointValue)
      return breakpointValue

    // Check in the reverse of applicable breakpoints to see if there is a media
    let applicableBreakpoints = getApplicableBreakpoints(
      windowWidth.value,
    ).reverse()

    for (const applicableBreakpoint of applicableBreakpoints) {
      const applicableBreakpointMedia = mediaField.value.find(
        b => b.breakpoint === applicableBreakpoint.name,
      )
      if (applicableBreakpointMedia)
        return applicableBreakpointMedia
    }

    applicableBreakpoints = getGreaterThanBreakpoints(
      windowWidth.value,
    )

    // Check the reverse (we do this because maybe they set the "lg" but not the "default")
    for (const applicableBreakpoint of applicableBreakpoints) {
      const applicableBreakpointMedia = mediaField.value.find(
        b => b.breakpoint === applicableBreakpoint.name,
      )
      if (applicableBreakpointMedia)
        return applicableBreakpointMedia
    }
  }
  // If we still haven't found something search the other way

  return null
}

function generateMediaSrcSetUrl(mediaBreakpoint?: MediaFieldPropertiesBreakpointInterface, breakpointConfig?: MediaFieldBreakpointConfigInterface): string {
  if (typeof mediaBreakpoint !== 'undefined' && typeof breakpointConfig !== 'undefined' && typeof mediaBreakpoint === 'object') {
    let json: string | object = typeof mediaBreakpoint.modifiers === 'undefined' ? {} : mediaBreakpoint.modifiers

    if (typeof json === 'string') {
      json = JSON.parse(json)
    }

    json = Object.assign({ auto: 'compress,format', cs: 'srgb', fit: 'crop' }, json)

    if (typeof json !== 'string') {
      json = JSON.stringify(json)
    }

    const modifiers = JSON.parse(json)

    modifiers.w = breakpointConfig.width
    modifiers.h = breakpointConfig.height

    return nuxtImage(mediaBreakpoint.url, modifiers, { provider: mediaBreakpoint.provider })
  }
  return ''
}

const pictureSources = computed(() => {
  if (props.field === null)
    return null

  // Return an array of sources and their min / max width
  // The first source will be the default image and should have no min
  // The rest will be the breakpoints with the last one not having a max
  const sources = []
  const mediaConfig = orderedMediaFieldConfig.value

  if (mediaConfig === undefined || mediaConfig === null) {
    console.error('No breakpoints found for field:', props.field)
    return []
  }

  let i = 0
  for (const key of Object.keys(mediaConfig)) {

    const media = getBreakpointValue(key)
    const config = getBreakpointConfig(key)

    if (!media || !config)
      continue

    const source: { src: string, media: string, breakpoint: string } = {
      src: generateMediaSrcSetUrl(media, config),
      media: '',
      breakpoint: key,
    }

    const isFirst = i === 0
    const isLast = i === Object.keys(mediaConfig).length - 1

    const breakpointSettings = breakpointStore.breakpoints.find(
      breakpoint => breakpoint.name === key,
    )

    if (!isFirst && breakpointSettings) {
      source.media = `(min-width: ${breakpointSettings.minWidth}px)`
    }

    const nextBreakpointKey = Object.keys(mediaConfig)[i + 1]
    const nextBreakpointSettings = breakpointStore.breakpoints.find(
      breakpoint => breakpoint.name === nextBreakpointKey,
    )

    if (!isLast && nextBreakpointSettings) {
      source.media = source.media !== '' ? `${source.media}, (max-width: ${nextBreakpointSettings.minWidth - 1}px)` : `(max-width: ${nextBreakpointSettings.minWidth - 1}px)`
    }

    sources.push(source)
    i++
  }

  return sources
})

// This is to detect if the parent is the first element in the loop so
// we can apply fetchpriority=high and remove Lazy Loading
const parentIsFirstElement = inject('parentIsFirstElement', false)

const currentMedia = computed(() => {
  if (props.currentBreakpoint === undefined)
    return null

  return getBreakpointValue(props.currentBreakpoint)
})

const currentConfig = computed(() => {
  if (props.currentBreakpoint === undefined)
    return null

  return getBreakpointConfig(props.currentBreakpoint)
})

const currentModifiers = computed(() => {
  let json: string | object = typeof currentMedia.value?.modifiers === 'undefined' ? {} : currentMedia.value.modifiers

  if (typeof json === 'string') {
    json = JSON.parse(json)
  }
  json = Object.assign({ auto: 'compress,format', cs: 'srgb', fit: 'crop' }, json)

  if (typeof json !== 'string') {
    json = JSON.stringify(json)
  }

  return JSON.parse(json)
})

const renderFormat = computed(() => {
  const media = currentMedia.value as currentMediaObjectInterface
  if (media && media.type && media?.type === 'video')
    return 'Video'

  return 'Image'
})

const intrensicStyles = computed(() => {
  if (currentMedia.value === null || currentMedia.value.modifiers === undefined)
    return {}

  if (currentMedia.value.modifiers.fit !== 'contain')
    return {}

  if (currentMedia.value.modifiers.w >= currentMedia.value.modifiers.h) {
    return {
      height: `${currentMedia.value.modifiers.h}px`,
    }
  }
  else {
    return {
      width: `${currentMedia.value.modifiers.w}px`,
    }
  }
})

const backgroundClasses = computed(() => {
  if (props.background === false || currentMedia.value === null)
    return []

  const classes: Array<string> = []

  if (currentMedia.value.modifiers === undefined)
    return classes

  let json: string | object = typeof currentMedia.value.modifiers === 'undefined' ? {} : currentMedia.value.modifiers
  if (typeof json !== 'string') {
    json = JSON.stringify(json)
  }

  const modifiers = JSON.parse(json)

  if (modifiers.position)
    classes.push(`bg-${modifiers.position}`)

  if (modifiers.fit) {
    // Tailwinds background 'none' is 'auto' which is the closest thing to none
    const fit = modifiers.fit === 'none' ? 'auto' : modifiers.fit
    classes.push(`bg-${fit}`)
  }

  return classes
})

function mediaLoaded() {
  emit('load', { src: pictureSources, currentMedia: currentMedia.value })
}
</script>

<template>
  <template v-if="field && renderFormat === 'Image' && pictureSources">
    <template v-if="!background">
      <picture
        v-bind="$attrs"
        :class="backgroundClasses"
        class="voix-media"
      >
        <source
          v-for="(source, index) in pictureSources"
          :key="index"
          :srcset="source.src"
          :media="source.media"
        >
        <img
          :src="pictureSources[0]?.src"
          v-bind="$attrs"
          :alt="currentMedia?.title"
          :loading="parentIsFirstElement ? 'eager' : loading"
          :fetchpriority="parentIsFirstElement ? 'high' : 'auto'"
          :class="backgroundClasses"
          :style="intrensicStyles"
          @load="mediaLoaded"
        >
      </picture>
    </template>
    <template v-else-if="currentMedia && currentConfig">
      <slot
        :image="generateMediaSrcSetUrl(currentMedia, currentConfig)"
        v-bind="$attrs"
        :background-classes="backgroundClasses"
        :style="[intrensicStyles, { backgroundImage: `url(${generateMediaSrcSetUrl(currentMedia, currentConfig)})` }]"
        :loading="parentIsFirstElement ? 'eager' : loading"
        :fetchpriority="parentIsFirstElement ? 'high' : 'auto'"
        :alt="currentMedia?.title"
        :class="backgroundClasses"
        :modifiers="currentModifiers"
      />
    </template>
  </template>

  <template v-if="renderFormat === 'Video' && currentMedia && currentConfig">
    <VoixYouTube
      v-if="currentMedia.provider === 'youtube'"
      v-slot="{ play, pause, paused, mute, unMute, muted, volume, setVolume, fullscreen }"
      v-bind="$attrs"
      :src="currentMedia.url"
      :width="currentConfig.width"
      :height="currentConfig.height"
      :options="currentModifiers"
    >
      <slot
        :play="play"
        :pause="pause"
        :paused="paused"
        :mute="mute"
        :un-mute="unMute"
        :muted="muted"
        :volume="volume"
        :set-volume="setVolume"
        :fullscreen="fullscreen"
      />
    </VoixYouTube>

    <VoixVimeo
      v-else-if="currentMedia.provider === 'vimeo'"
      v-slot="{ play, pause, paused, mute, unMute, muted, volume, setVolume, fullscreen }"
      v-bind="$attrs"
      :src="currentMedia.url"
      :width="currentConfig.width"
      :height="currentConfig.height"
      :options="currentModifiers"
    >
      <slot
        :play="play"
        :pause="pause"
        :paused="paused"
        :mute="mute"
        :un-mute="unMute"
        :muted="muted"
        :volume="volume"
        :set-volume="setVolume"
        :fullscreen="fullscreen"
      />
    </VoixVimeo>

    <VoixWistia
      v-else-if="currentMedia.provider === 'wistia'"
      v-slot="{ play, pause, paused, mute, unMute, muted, volume, setVolume, fullscreen }"
      v-bind="$attrs"
      :src="currentMedia.url"
      :width="currentConfig.width"
      :height="currentConfig.height"
      :options="currentModifiers"
    >
      <slot
        :play="play"
        :pause="pause"
        :paused="paused"
        :mute="mute"
        :un-mute="unMute"
        :muted="muted"
        :volume="volume"
        :set-volume="setVolume"
        :fullscreen="fullscreen"
      />
    </VoixWistia>

    <VoixCloudflare
      v-else-if="currentMedia.provider === 'cloudflare'"
      v-slot="{ play, pause, paused, mute, unMute, muted, volume, setVolume, fullscreen }"
      v-bind="$attrs"
      :src="currentMedia.url"
      :width="currentConfig.width"
      :height="currentConfig.height"
      :options="currentModifiers"
    >
      <slot
        :play="play"
        :pause="pause"
        :paused="paused"
        :mute="mute"
        :un-mute="unMute"
        :muted="muted"
        :volume="volume"
        :set-volume="setVolume"
        :fullscreen="fullscreen"
      />
    </VoixCloudflare>
  </template>

  <div
    v-if="debug"
    class="space-y-4 mt-[25px] bg-white p-8 px-8 rounded-sm whitespace-pre-line break-words max-w-[100vw] overflow-x-scroll"
  >
    <ClientOnly>
      <h1 class="text-2xl">
        Media Field Debug
      </h1>
      <div v-if="renderFormat === 'Image'">
        <strong>Picture Sources:</strong>
        <pre>{{ pictureSources }}</pre>
      </div>
      <div>
        <strong>Media to use:</strong>
        <pre>{{ currentMedia }}</pre>
      </div>
      <div>
        <strong>Config To Use:</strong>
        <pre>{{ currentConfig }}</pre>
      </div>
      <div>
        <strong>Current Modifiers:</strong>
        <pre>{{ currentModifiers }}</pre>
      </div>
      <div>
        <strong>Render Format:</strong>
        <pre>{{ renderFormat }}</pre>
      </div>
      <div>
        <strong>Background:</strong>
        <pre>{{ background }}</pre>
      </div>
      <div>
        <strong>Background Classes:</strong>
        <pre>{{ backgroundClasses }}</pre>
      </div>
      <div>
        <strong>Parent is first element:</strong>
        <pre>{{ parentIsFirstElement }}</pre>
      </div>
    </ClientOnly>
  </div>
</template>
